/**
 * @file   src\components\Inventory\EditInventory\InventoryLotLandLocation.js
 * @brief  This file is responsible for the view of inventory lot land type location.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import { handleTextBoxChange } from "@/actions/common";
import RadioButtonList from "@/components/Common/RadioButtonList";
import {
    createS3Folder,
    itemList,
    replaceComma,
    thousandSeperatedValue,
} from "@/helpers/common";
import {
    approvedStrctrSection,
    floodPlainSection,
    INVENTORY_RT_PRVT_DRIVE,
    INVENTORY_WS_DRLLD_WELL,
    INVENTORY_WWS_SEPTIC_SYS,
    LOT_TYPES,
    poweAccessSection,
    ROAD_TYPES,
    RT_PVT_DRIVE_INSTALL,
    WASTE_WATER_SYSTEM,
    WATER_SUPPLY,
    WS_DRILLED_WELL_APPRRV,
    WS_DRILLED_WELL_INSTALL,
    WWS_SEPT_TANK_APPROV,
    WWS_SEPT_TANK_INSTALL,
    YES_NO_OPTIONS,
} from "@/constants/common";
import TextBox from "@/components/Common/TextBox";
import SelectDropDown from "@/components/Common/SelectDropDown";
import { convertToNumber } from "@/helpers/validations";


export const InventoryLotLandLocationForm = (props) => {

    const {
        formValues,
        handleFormValues,
        isEdit = false,
    } = props;

    const locationAddress = _.get(formValues, "locAddress", null)
    const acreage = _.get(formValues, "acreage", null)
    const lotType = convertToNumber(_.get(formValues, "lotType", null))
    const roadType = convertToNumber(_.get(formValues, "roadType", null))
    const isPowerAccess = _.get(formValues, "isPowerAccess", null)
    const waterSuply = convertToNumber(_.get(formValues, "waterSuply", null))
    const wasteWtrSys = convertToNumber(_.get(formValues, "wasteWtrSys", null))
    const isApprovedStrctr = _.get(formValues, "isApprovedStrctr", null)
    const isInFloodPlain = _.get(formValues, "isInFloodPlain", null)
    const isPrvtDrInst = _.get(formValues, "isPrvtDrInst", null)
    const isDrldWelInst = _.get(formValues, "isDrldWelInst", null)
    const isDrldWelAppr = _.get(formValues, "isDrldWelAppr", null)
    const isSepTankInst = _.get(formValues, "isSepTankInst", null)
    const isSepTankAppr = _.get(formValues, "isSepTankAppr", null)
    const acre_Sqft = _.get(formValues, "acre_Sqft", null)

    const options = [
        { value: 1, label: "Acres" },
        { value: 2, label: "SQFT" },
    ];

    // To update the state values
    const updateValuesToState = (key, value) => {
        handleFormValues({ [key]: value });
    };

    // Lot size change event handler
    const handleLotSizeChanges = (e) => {
        const value = handleTextBoxChange(e);
        updateValuesToState("acreage", replaceComma(value));
        updateValuesToState("acre_Sqft", acre_Sqft ? acre_Sqft : 1);
    };

    // Lot size type selection change
    const handleLotSizeTypeSelection = (e) => {
        const values = _.get(e, "value", "");
        updateValuesToState("acre_Sqft", values);
    };

    // Lot type change handler
    const handleLotTypeChange = (e) => {
        const selectedValue = convertToNumber(e.target.value);
        updateValuesToState("lotType", selectedValue);
    };

    // road type change event handler
    const handleRoadTypeChange = (e) => {
        const selectedValue = convertToNumber(e.target.value);
        let stateToBeUpdated = {};
        if (selectedValue === roadType) {
            stateToBeUpdated = { roadType: "", isPrvtDrInst: "" };
        } else {
            stateToBeUpdated = { roadType: selectedValue, isPrvtDrInst: "" };
        }
        handleFormValues(stateToBeUpdated);
    };

    // water supply change event handler
    const handleWaterSupplyChange = (e) => {
        const selectedValue = convertToNumber(e.target.value);
        let stateToBeUpdated = {};
        if (selectedValue === waterSuply) {
            stateToBeUpdated = {
                waterSuply: "",
                isDrldWelInst: "",
                isDrldWelAppr: "",
            };
        } else {
            stateToBeUpdated = {
                waterSuply: selectedValue,
                isDrldWelInst: "",
                isDrldWelAppr: "",
            };
        }
        handleFormValues(stateToBeUpdated);
    };

    // waste water change event handler
    const handleWasteWaterChange = (e) => {
        const selectedValue = convertToNumber(e.target.value);
        let stateToBeUpdated = {};
        if (selectedValue === wasteWtrSys) {
            stateToBeUpdated = {
                wasteWtrSys: "",
                isSepTankAppr: "",
                isSepTankInst: "",
            };
        } else {
            stateToBeUpdated = {
                wasteWtrSys: selectedValue,
                isSepTankAppr: "",
                isSepTankInst: "",
            };
        }
        handleFormValues(stateToBeUpdated);
    };

    // option handling for poweAccessSection, approvedStrctrSection and floodPlainSection
    const handleBooleanOptionSelection = (e, section) => {
        const selectedValue = e.target.value;
        switch (section) {
            case poweAccessSection:
                if (selectedValue === isPowerAccess) {
                    updateValuesToState(poweAccessSection, "");
                } else {
                    updateValuesToState(poweAccessSection, selectedValue);
                }
                break;
            case approvedStrctrSection:
                if (selectedValue === isApprovedStrctr) {
                    updateValuesToState(approvedStrctrSection, "");
                } else {
                    updateValuesToState(approvedStrctrSection, selectedValue);
                }
                break;
            case floodPlainSection:
                if (selectedValue === isInFloodPlain) {
                    updateValuesToState(floodPlainSection, "");
                } else {
                    updateValuesToState(floodPlainSection, selectedValue);
                }
                break;
        }
    };

    // to load the view of poweAccessSection, approvedStrctrSection and floodPlainSection
    const loadBooleanOptioLists = (section) => {
        const itemList = YES_NO_OPTIONS;
        let isCheckedYes = false;
        let isCheckedNo = false;
        switch (section) {
            case poweAccessSection:
                if (isPowerAccess === "true" || isPowerAccess === 1) {
                    isCheckedYes = true;
                } else if (isPowerAccess === "false" || isPowerAccess === 0) {
                    isCheckedNo = true;
                }
                break;
            case approvedStrctrSection:
                if (isApprovedStrctr === "true" || isApprovedStrctr === 1) {
                    isCheckedYes = true;
                } else if (isApprovedStrctr === "false" || isApprovedStrctr === 0) {
                    isCheckedNo = true;
                }
                break;
            case floodPlainSection:
                if (isInFloodPlain === "true" || isInFloodPlain === 1) {
                    isCheckedYes = true;
                } else if (isInFloodPlain === "false" || isInFloodPlain === 0) {
                    isCheckedNo = true;
                }
                break;
        }

        return (
            <>
                <div className="col-md-6">
                    <label className="radio-wrap mb-3">
                        <input
                            name={`${section}-radio`}
                            type="radio"
                            value={_.get(itemList[0], "value", "")}
                            checked={isCheckedYes}
                            onClick={(e) => handleBooleanOptionSelection(e, section)}
                        />
                        <span>
                            <div className="timing-wrap-info">
                                <h2 className="pb-0">{_.get(itemList[0], "text", "")}</h2>
                            </div>
                        </span>
                    </label>
                </div>

                <div className="col-md-6">
                    <label className="radio-wrap mb-3">
                        <input
                            name={`${section}-radio`}
                            type="radio"
                            value={_.get(itemList[1], "value", "")}
                            checked={isCheckedNo}
                            onClick={(e) => handleBooleanOptionSelection(e, section)}
                        />
                        <span>
                            <div className="timing-wrap-info">
                                <h2 className="pb-0">{_.get(itemList[1], "text", "")}</h2>
                            </div>
                        </span>
                    </label>
                </div>
            </>
        );
    };

    // To handle the checkbox event
    const handleCheckBoxSelection = (e) => {
        const key = e.target.name;
        handleFormValues({ [key]: e.target.checked });
    };

    // filter value of lot size
    const filterLotSizeArray = (value) => {
        if (options && value) {
            const filterValue = options.filter(function (lst) {
                return lst.value === value;
            });
            return filterValue;
        }
        return null;
    };

    return (
        <>
            <div className="wantad-location-wrap mb-3">
                <div className="row">
                    <div className="col-md-12">
                        <div className="common-input-wrap mb-0">
                            <Form.Label className="mb-2">Lot size</Form.Label>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="common-input-wrap mb-0">
                            <TextBox
                                type="text"
                                className="form-control"
                                value={thousandSeperatedValue(acreage) || ""}
                                onChange={(e) => {
                                    handleLotSizeChanges(e);
                                }}
                                placeholder="Total"
                            ></TextBox>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="common-input-wrap mb-0">
                            <SelectDropDown
                                name="colors3"
                                options={options || []}
                                className="lotsize-select"
                                classNamePrefix="select"
                                value={filterLotSizeArray(acre_Sqft ? acre_Sqft : 1)}
                                onChange={(e) => handleLotSizeTypeSelection(e)}
                                components={{
                                    DropdownIndicator: () => null,
                                    IndicatorSeparator: () => null,
                                }}
                                styles={{
                                    dropdownIndicator: (provided, state) => ({
                                        ...provided,
                                        transform:
                                            state.selectProps.menuIsOpen && "rotate(180deg)",
                                    }),
                                }}
                                errorClass="error-msg error-msg-height"
                            />
                        </div>
                    </div>
                </div>
                <div className="wantad-timing-wrap">
                    <div className="land-radio-wrap pb-4">
                        <h6 className="red-marked-heading">
                            Lot type <span>*</span>
                        </h6>
                        <div className="row">
                            <RadioButtonList
                                list={itemList(LOT_TYPES)}
                                name="lt-radio"
                                seletedValue={lotType}
                                onClick={(e) => handleLotTypeChange(e)}
                            />
                        </div>
                    </div>

                    <div className="land-radio-wrap pb-4">
                        <h6>What type of road leads to your property?</h6>
                        <div className="row">
                            <RadioButtonList
                                list={itemList(ROAD_TYPES)}
                                name="rt-radio"
                                seletedValue={roadType}
                                onClick={(e) => handleRoadTypeChange(e)}
                            />
                            {roadType === INVENTORY_RT_PRVT_DRIVE && (
                                <div className="col-md-6 offset-md-6">
                                    <label
                                        className={
                                            !isEdit ? "check-wrap mb-0" : "check-wrap mt-3"
                                        }
                                    >
                                        <input
                                            type="checkbox"
                                            name={RT_PVT_DRIVE_INSTALL}
                                            onChange={handleCheckBoxSelection}
                                            checked={isPrvtDrInst}
                                        />
                                        <span>
                                            <div className="timing-wrap-info">
                                                <h2 className="pb-0">Installed</h2>
                                            </div>
                                        </span>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="land-radio-wrap pb-4">
                        <h6>Do you have power within 100' of your property?</h6>
                        <div className="row">
                            {loadBooleanOptioLists(poweAccessSection)}
                        </div>
                    </div>

                    <div className="land-radio-wrap pb-4">
                        <h6>Water Supply</h6>
                        <div className="row">
                            <RadioButtonList
                                list={itemList(WATER_SUPPLY)}
                                name="ws-radio"
                                seletedValue={waterSuply}
                                onClick={(e) => handleWaterSupplyChange(e)}
                            />
                            {waterSuply === INVENTORY_WS_DRLLD_WELL && (
                                <>
                                    <div className="col-md-6 offset-md-6">
                                        <label
                                            className={
                                                !isEdit ? "check-wrap mb-3" : "check-wrap mt-3"
                                            }
                                        >
                                            <input
                                                type="checkbox"
                                                name={WS_DRILLED_WELL_INSTALL}
                                                onChange={handleCheckBoxSelection}
                                                checked={isDrldWelInst}
                                            />
                                            <span>
                                                <div className="timing-wrap-info">
                                                    <h2 className="pb-0">Installed</h2>
                                                </div>
                                            </span>
                                        </label>
                                    </div>

                                    <div className="col-md-6 offset-md-6">
                                        <label
                                            className={
                                                !isEdit ? "check-wrap mb-3" : "check-wrap mt-3"
                                            }
                                        >
                                            <input
                                                type="checkbox"
                                                name={WS_DRILLED_WELL_APPRRV}
                                                onChange={handleCheckBoxSelection}
                                                checked={isDrldWelAppr}
                                            />
                                            <span>
                                                <div className="timing-wrap-info">
                                                    <h2 className="pb-0">Approved</h2>
                                                </div>
                                            </span>
                                        </label>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="land-radio-wrap pb-4">
                        <h6>Wastewater System </h6>
                        <div className="row">
                            <RadioButtonList
                                list={itemList(WASTE_WATER_SYSTEM)}
                                name="wws-radio"
                                seletedValue={wasteWtrSys}
                                onClick={(e) => handleWasteWaterChange(e)}
                            />
                            {wasteWtrSys === INVENTORY_WWS_SEPTIC_SYS && (
                                <>
                                    <div className="col-md-6 offset-md-6">
                                        <label
                                            className={
                                                !isEdit ? "check-wrap mb-3" : "check-wrap mt-3"
                                            }
                                        >
                                            <input
                                                type="checkbox"
                                                name={WWS_SEPT_TANK_INSTALL}
                                                onChange={handleCheckBoxSelection}
                                                checked={isSepTankInst}
                                            />
                                            <span>
                                                <div className="timing-wrap-info">
                                                    <h2 className="pb-0">Installed</h2>
                                                </div>
                                            </span>
                                        </label>
                                    </div>
                                    <div className="col-md-6 offset-md-6">
                                        <label
                                            className={
                                                !isEdit ? "check-wrap mb-0" : "check-wrap mt-3"
                                            }
                                        >
                                            <input
                                                type="checkbox"
                                                name={WWS_SEPT_TANK_APPROV}
                                                onChange={handleCheckBoxSelection}
                                                checked={isSepTankAppr}
                                            />
                                            <span>
                                                <div className="timing-wrap-info">
                                                    <h2 className="pb-0">Approved</h2>
                                                </div>
                                            </span>
                                        </label>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="land-radio-wrap pb-4">
                        <h6>Is your property approved for a structure?</h6>
                        <div className="row">
                            {" "}
                            {loadBooleanOptioLists(approvedStrctrSection)}
                        </div>
                    </div>

                    <div className="land-radio-wrap pb-4">
                        <h6>Is your property on a flood plain?</h6>
                        <div className="row">
                            {loadBooleanOptioLists(floodPlainSection)}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}